import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
// import L from 'leaflet';
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';
import './MeasroDashboard.css';
import ReportTop from './ReportTop';
import ReportLeftData from './ReportLeftData';
import ReportZone from './ReportZone';
import PolygonCoverage from './PolygonCoverage';
import LineCoverage from './LineCoverage';
import PointCoverage from './PointCoverage';

import { baseurl, imgurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';
import ExportReport from './ExportReport';
 import L from 'leaflet';
import 'leaflet-rotate';

// =====================================================================
// Constants
// =====================================================================
const NEARMAP_API_KEY = 'NWI2ZWIzNTYtODdmMS00YmIyLTk5ZGItZDkwNTVjMDM2MzFi';
const MAPBOX_TOKEN = 'pk.eyJ1IjoibWVhc3JvYWRtaW5tYXBib3giLCJhIjoiY21wYzQybXVrMDN5NDJ1c2JkZGhmYWFsNiJ9.aI89Wt7FHAYw3FUonCbKkw';
const NEARMAP_CONTENT_TYPE = 'Vert';
const BUFFER_METERS = 20;

const DEFAULT_QUALITY_ZOOM = 19;
const SHARPER_QUALITY_ZOOM = 20;
const HIGH_QUALITY_ZOOM = 21;
const ZOOM_UPGRADE_THRESHOLD = 1.4;
const NEARMAP_TILE_SIZE = 256;
const ZOOM_DEBOUNCE_MS = 350;


const NEARMAP_TILE_WIDTH = 256;
const NEARMAP_TILE_HEIGHT_VERT = 256;
const NEARMAP_TILE_HEIGHT_OBLIQUE = 192; // per Nearmap docs: panorama tiles render at 256x192, not 256x256

function tileHeightFor(contentType) {
    return contentType === 'Vert' ? NEARMAP_TILE_HEIGHT_VERT : NEARMAP_TILE_HEIGHT_OBLIQUE;
}

const BEARING_BY_DIRECTION = {
    Vert: 0,
    North: 0,
    East: -90,
    South: 180,
    West: 90,
};

// Add alongside the other constants
const NEARMAP_DIRECTIONS = [
    { key: 'Vert', label: 'Top-down' },
    { key: 'North', label: 'North' },
    { key: 'South', label: 'South' },
    { key: 'East', label: 'East' },
    { key: 'West', label: 'West' },
];

 
const NEARMAP_COVERAGE_URL = (lat, lng) =>
    `https://api.nearmap.com/coverage/v2/point/${lng},${lat}?apikey=${NEARMAP_API_KEY}&limit=10`;

// =====================================================================
// Tile math helpers
// =====================================================================
const lon2tileX = (lon, z) => (lon + 180) / 360 * Math.pow(2, z);
const lat2tileY = (lat, z) => {
    const rad = lat * Math.PI / 180;
    return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, z);
};

// const nearmapTileUrl = (z, x, y, surveyId) =>
//     surveyId
//         ? `https://api.nearmap.com/tiles/v3/surveys/${surveyId}/${NEARMAP_CONTENT_TYPE}/${z}/${x}/${y}.jpg?apikey=${NEARMAP_API_KEY}`
//         : `https://api.nearmap.com/tiles/v3/${NEARMAP_CONTENT_TYPE}/${z}/${x}/${y}.jpg?apikey=${NEARMAP_API_KEY}`;



const nearmapTileUrl = (z, x, y, surveyId, contentType = NEARMAP_CONTENT_TYPE) =>
    surveyId
        ? `https://api.nearmap.com/tiles/v3/surveys/${surveyId}/${contentType}/${z}/${x}/${y}.jpg?apikey=${NEARMAP_API_KEY}`
        : `https://api.nearmap.com/tiles/v3/${contentType}/${z}/${x}/${y}.jpg?apikey=${NEARMAP_API_KEY}`;


// =====================================================================
// CORS-safe tile fetch — fetch() + createImageBitmap instead of
// new Image() + crossOrigin. An Image element still taints the canvas
// if Nearmap's tile response is missing/mismatched CORS headers, and
// canvas.toBlob() then silently resolves null with NO thrown error —
// which is exactly why the overlay was never appearing. A bitmap built
// from a locally-fetched Blob is never cross-origin-tainted.
// =====================================================================
async function fetchTileBitmap(url) {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await createImageBitmap(blob);
    } catch (err) {
        return null;
    }
}

// =====================================================================
// In-memory + localStorage cache, keyed per zoom tier so switching
// between quality levels doesn't refetch on every zoom in/out
// =====================================================================
const nearmapImageCache = new Map();

// function getNearmapCacheKey(bufferedFeature, surveyId, zoom) {
//     const bbox = turf.bbox(bufferedFeature).map((n) => n.toFixed(5)).join(',');
//     return `nearmap_img_${bbox}_${surveyId || 'latest'}_z${zoom}`;
// }


function getNearmapCacheKey(bufferedFeature, surveyId, zoom, contentType = NEARMAP_CONTENT_TYPE) {
    const bbox = turf.bbox(bufferedFeature).map((n) => n.toFixed(5)).join(',');
    return `nearmap_img_${bbox}_${surveyId || 'latest'}_${contentType}_z${zoom}`;
}



function readNearmapCache(key) {
    if (nearmapImageCache.has(key)) return nearmapImageCache.get(key);
    try {
        const stored = localStorage.getItem(key);
        if (stored) {
            nearmapImageCache.set(key, stored);
            return stored;
        }
    } catch (e) {
        // localStorage unavailable — in-memory cache still works
    }
    return null;
}

function writeNearmapCache(key, dataUrl) {
    nearmapImageCache.set(key, dataUrl);
    const persist = () => {
        try {
            localStorage.setItem(key, dataUrl);
        } catch (e) {
            // Quota exceeded — skip persistence, session cache still holds it
        }
    };
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(persist, { timeout: 2000 });
    } else {
        setTimeout(persist, 0);
    }
}



// =====================================================================
// pickBaseZoomForArea — lowered the default tier from 19 to 18 so the
// FIRST image (the one blocking initial page load) is smaller and
// composites noticeably faster. The sharper/high tiers still kick in
// automatically once the user zooms in, exactly as before.
// =====================================================================
function pickBaseZoomForArea(bufferedFeature) {
    const areaSqM = turf.area(bufferedFeature);
    if (areaSqM > 200000) return 16;
    if (areaSqM > 50000) return 17;
    if (areaSqM > 10000) return 18;
    return 18; // was DEFAULT_QUALITY_ZOOM (19) — one step lighter for faster first paint
}


// =====================================================================
// Compositor worker — moves tile fetch + decode + draw entirely off the
// main thread using OffscreenCanvas, so the page stays responsive while
// compositing. Also switches output to JPEG instead of PNG — JPEG
// encodes much faster and produces a fraction of the file size for
// photographic satellite tiles (PNG's lossless compression is overkill
// here and was a major contributor to load time).
// =====================================================================
const compositorWorkerSrc = `
self.onmessage = async function (e) {
    const { id, tiles, canvasW, canvasH, scale } = e.data;
    try {
        const canvas = new OffscreenCanvas(canvasW, canvasH);
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        const CONCURRENCY = 10;
        let anyTileLoaded = false;
        for (let i = 0; i < tiles.length; i += CONCURRENCY) {
            const batch = tiles.slice(i, i + CONCURRENCY);
            const loaded = await Promise.all(
                batch.map(async (t) => {
                    try {
                        const res = await fetch(t.url);
                        if (!res.ok) return null;
                        const blob = await res.blob();
                        const bitmap = await createImageBitmap(blob);
                        return { ...t, bitmap };
                    } catch {
                        return null;
                    }
                })
            );
            for (const tile of loaded) {
                if (!tile) continue;
                anyTileLoaded = true;
                ctx.drawImage(tile.bitmap, tile.dx, tile.dy, tile.tileW * scale, tile.tileH * scale);
                tile.bitmap.close();
            }
        }

        if (!anyTileLoaded) {
            self.postMessage({ id, error: 'no tiles loaded' });
            return;
        }

        const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.88 });
        const buf = await blob.arrayBuffer();
        self.postMessage({ id, buf }, [buf]);
    } catch (err) {
        self.postMessage({ id, error: err.message });
    }
};
`;

let compositorWorker = null;
function getCompositorWorker() {
    if (compositorWorker) return compositorWorker;
    const blob = new Blob([compositorWorkerSrc], { type: 'application/javascript' });
    compositorWorker = new Worker(URL.createObjectURL(blob));
    return compositorWorker;
}

let compositeRequestCounter = 0;
function compositeInWorker(tiles, canvasW, canvasH, scale) {
    return new Promise((resolve) => {
        const worker = getCompositorWorker();
        const id = ++compositeRequestCounter;

        const handleMessage = (e) => {
            if (e.data.id !== id) return;
            worker.removeEventListener('message', handleMessage);
            if (e.data.error) {
                resolve(null);
                return;
            }
            const blob = new Blob([e.data.buf], { type: 'image/jpeg' });
            resolve(blob);
        };

        worker.addEventListener('message', handleMessage);
        worker.postMessage({ id, tiles, canvasW, canvasH, scale });
    });
}

// =====================================================================
// compositeBboxImage — now delegates fetch/decode/draw to the worker.
// Main thread only computes tile URLs/positions and converts the
// returned JPEG blob to a data URL. Auto-downgrade logic kept as-is.
// =====================================================================
const MAX_CANVAS_DIM = 8000;

async function compositeBboxImage(bufferedFeature, zoom, surveyId, contentType = NEARMAP_CONTENT_TYPE) {
    const bbox = turf.bbox(bufferedFeature);
    let z = zoom;
    let SCALE = z >= HIGH_QUALITY_ZOOM ? 2 : 1;




    const computeDims = (zz, scale) => {
        const worldMinX = lon2tileX(bbox[0], zz) * NEARMAP_TILE_SIZE;
        const worldMaxX = lon2tileX(bbox[2], zz) * NEARMAP_TILE_SIZE;
        const worldMinY = lat2tileY(bbox[3], zz) * NEARMAP_TILE_SIZE;
        const worldMaxY = lat2tileY(bbox[1], zz) * NEARMAP_TILE_SIZE;
        return {
            worldMinX, worldMaxX, worldMinY, worldMaxY,
            canvasW: Math.ceil((worldMaxX - worldMinX) * scale),
            canvasH: Math.ceil((worldMaxY - worldMinY) * scale),
        };
    };

    let dims = computeDims(z, SCALE);

    while ((dims.canvasW > MAX_CANVAS_DIM || dims.canvasH > MAX_CANVAS_DIM) && (SCALE > 1 || z > 15)) {
        if (SCALE > 1) SCALE = 1;
        else z -= 1;
        dims = computeDims(z, SCALE);
    }

    if (dims.canvasW <= 0 || dims.canvasH <= 0 || dims.canvasW > MAX_CANVAS_DIM || dims.canvasH > MAX_CANVAS_DIM) {
        console.error('Nearmap composite: canvas dims still out of range after auto-downgrade', dims.canvasW, dims.canvasH);
        return null;
    }

    const { worldMinX, worldMaxX, worldMinY, worldMaxY, canvasW, canvasH } = dims;

    const xMin = Math.floor(worldMinX / NEARMAP_TILE_SIZE);
    const xMax = Math.floor(worldMaxX / NEARMAP_TILE_SIZE);
    const yMin = Math.floor(worldMinY / NEARMAP_TILE_SIZE);
    const yMax = Math.floor(worldMaxY / NEARMAP_TILE_SIZE);

    // const tiles = [];
    // for (let tx = xMin; tx <= xMax; tx++) {
    //     for (let ty = yMin; ty <= yMax; ty++) {
    //         tiles.push({
    //             url: nearmapTileUrl(z, tx, ty, surveyId, contentType), // ← now passes contentType through
    //             dx: (tx * NEARMAP_TILE_SIZE - worldMinX) * SCALE,
    //             dy: (ty * NEARMAP_TILE_SIZE - worldMinY) * SCALE,
    //         });
    //     }
    // }

        const tileH = tileHeightFor(contentType);

const tiles = [];
for (let tx = xMin; tx <= xMax; tx++) {
    for (let ty = yMin; ty <= yMax; ty++) {
        tiles.push({
            url: nearmapTileUrl(z, tx, ty, surveyId, contentType),
            dx: (tx * NEARMAP_TILE_WIDTH - worldMinX) * SCALE,
            dy: (ty * NEARMAP_TILE_WIDTH - worldMinY) * SCALE, // world grid position still uses square tile math (Slippy grid is always square)
            tileW: NEARMAP_TILE_WIDTH,
            tileH: NEARMAP_TILE_WIDTH, // actual draw height differs for oblique
        });
    }
}


    // const blob = await compositeInWorker(tiles, canvasW, canvasH, NEARMAP_TILE_SIZE, SCALE);
    const blob = await compositeInWorker(tiles, canvasW, canvasH, SCALE);
    if (!blob) {
        console.error('Nearmap composite: worker returned no image');
        return null;
    }

    const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });

    return {
        dataUrl,
        bounds: L.latLngBounds([[bbox[1], bbox[0]], [bbox[3], bbox[2]]]),
        actualZoom: z,
    };
}

// =====================================================================
// NearmapClipLeafletLayer — bbox rectangle overlay, CORS-safe, staged
// quality upgrade on zoom, debounced, always brought to front, and
// reports loading state so the UI can show a spinner
// =====================================================================
const NearmapClipLeafletLayer = L.Layer.extend({
   initialize: function (bufferedFeature, surveyId, onImageReady, onLoadingChange, contentType = NEARMAP_CONTENT_TYPE) {
    this._bufferedFeature = bufferedFeature;
    this._surveyId = surveyId || null;
    this._contentType = contentType;
    this._overlay = null;
    this._compositeZoom = null;
    this._loadToken = 0;
    this._onImageReady = onImageReady || null;
    this._onLoadingChange = onLoadingChange || null;
    this._zoomDebounceTimer = null;
    this._onZoomEnd = this._onZoomEnd.bind(this);
},
    onAdd: function (map) {
    this._map = map;
    map.on('zoomend', this._onZoomEnd);
    this._renderAtZoom(pickBaseZoomForArea(this._bufferedFeature)); // ← changed
    return this;
},
    onRemove: function (map) {
        map.off('zoomend', this._onZoomEnd);
        if (this._zoomDebounceTimer) clearTimeout(this._zoomDebounceTimer);
        if (this._overlay) {
            map.removeLayer(this._overlay);
            this._overlay = null;
        }
        if (this._onLoadingChange) this._onLoadingChange(false);
    },
    _nextTier: function () {
        if (this._compositeZoom < SHARPER_QUALITY_ZOOM) return SHARPER_QUALITY_ZOOM;
        if (this._compositeZoom < HIGH_QUALITY_ZOOM) return HIGH_QUALITY_ZOOM;
        return null;
    },
    _onZoomEnd: function () {
        const map = this._map;
        if (!map || this._compositeZoom == null) return;

        if (this._zoomDebounceTimer) clearTimeout(this._zoomDebounceTimer);
        this._zoomDebounceTimer = setTimeout(() => {
            const mapZoom = map.getZoom();
            const nextZoom = this._nextTier();
            if (nextZoom && mapZoom - this._compositeZoom >= ZOOM_UPGRADE_THRESHOLD) {
                this._renderAtZoom(nextZoom);
            }
        }, ZOOM_DEBOUNCE_MS);
    },
    // _renderAtZoom — must use this._contentType, NOT a bare contentType
_renderAtZoom: async function (zoom) {
    const map = this._map;
    if (!map) return;
    const token = ++this._loadToken;

    const cacheKey = getNearmapCacheKey(this._bufferedFeature, this._surveyId, zoom, this._contentType); // ← this._contentType, not contentType
    const cached = readNearmapCache(cacheKey);

    let dataUrl, bounds;
    try {
        if (cached) {
            dataUrl = cached;
            const bbox = turf.bbox(this._bufferedFeature);
            bounds = L.latLngBounds([[bbox[1], bbox[0]], [bbox[3], bbox[2]]]);
        } else {
            if (this._onLoadingChange) this._onLoadingChange(true);

            const result = await compositeBboxImage(this._bufferedFeature, zoom, this._surveyId, this._contentType); // ← this._contentType, not contentType

            if (token !== this._loadToken || !this._map) {
                if (this._onLoadingChange && token === this._loadToken) this._onLoadingChange(false);
                return;
            }
            if (!result) {
                if (this._onLoadingChange) this._onLoadingChange(false);
                return;
            }
            dataUrl = result.dataUrl;
            bounds = result.bounds;
            writeNearmapCache(cacheKey, dataUrl);
        }

        if (token !== this._loadToken || !this._map) return;

        if (this._overlay) this._map.removeLayer(this._overlay);
        this._overlay = L.imageOverlay(dataUrl, bounds, {
            interactive: false,
            opacity: 1,
        }).addTo(this._map);

        if (this._overlay.bringToFront) this._overlay.bringToFront();

        this._compositeZoom = zoom;
        if (this._onLoadingChange) this._onLoadingChange(false);
        if (this._onImageReady) {
            this._onImageReady({ dataUrl, surveyId: this._surveyId, bounds, zoom });
        }
    } catch (err) {
        console.error('Nearmap _renderAtZoom failed:', err);
        if (this._onLoadingChange) this._onLoadingChange(false);
    }
},
    setSurveyId: function (surveyId) {
    if (this._surveyId === surveyId) return;
    this._surveyId = surveyId;
    this._compositeZoom = null;
    this._renderAtZoom(pickBaseZoomForArea(this._bufferedFeature)); // ← changed
},
setDirection: function (contentType) { // ← the parameter here is fine, it's local to setDirection
    if (this._contentType === contentType) return;
    this._contentType = contentType; // ← this._contentType, storing the parameter value
    this._compositeZoom = null;
    this._renderAtZoom(pickBaseZoomForArea(this._bufferedFeature));
},

});

// =====================================================================
// Misc helpers
// =====================================================================
const getFallbackColor = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return '#' + (hash & 0x00FFFFFF).toString(16).padStart(6, '0');
};

const calculatePolygonAreaSqft = (coords) => {
    if (!coords || coords.length === 0) return 0;
    const isArrayOfRings = Array.isArray(coords[0]) && Array.isArray(coords[0][0]);
    const rings = isArrayOfRings ? coords : [coords];
    if (rings[0].length < 3) return 0;

    const ringAreaSqMeters = (ring) => {
        let area = 0;
        const RADIUS = 6378137;
        for (let i = 0; i < ring.length; i++) {
            const p1 = ring[i];
            const p2 = ring[(i + 1) % ring.length];
            const lat1 = p1[1] * Math.PI / 180, lng1 = p1[0] * Math.PI / 180;
            const lat2 = p2[1] * Math.PI / 180, lng2 = p2[0] * Math.PI / 180;
            area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
        }
        return Math.abs(area * RADIUS * RADIUS / 2.0);
    };

    let areaSqMeters = ringAreaSqMeters(rings[0]);
    for (let i = 1; i < rings.length; i++) {
        areaSqMeters -= ringAreaSqMeters(rings[i]);
    }
    areaSqMeters = Math.max(areaSqMeters, 0);
    return areaSqMeters * 10.7639;
};

const createTooltip = () => {
    const tooltip = document.createElement('div');
    tooltip.style.cssText = `
        position:absolute;pointer-events:none;
        background:#fff;border-radius:8px;
        font-size:10px;white-space:nowrap;
        z-index:9999;display:none;
        box-shadow:0 4px 16px rgba(0,0,0,0.18);
        border:none;min-width:100px;
        overflow:hidden;padding:6px 8px;
        border-left:4px solid #6a11cb;
    `;
    return tooltip;
};

// =====================================================================
// ReportNew
// =====================================================================
const ReportNew = () => {
    const { id } = useParams();
    const mapDivRef = useRef(null);
    const mapInstance = useRef(null);

    const tooltipRef = useRef(null);

    const [property, setProperty] = useState({
        id: 'property-boundary', name: 'Lot area', sqft: 0, acre: 0,
        color: '#6a11cb', visible: false, unit: 'acre', coords: [], fillOpacity: 0
    });
    const [zones, setZones] = useState([]);
    const [polygons, setPolygons] = useState([]);
    const [lines, setLines] = useState([]);
    const [points, setPoints] = useState([]);
    const [projectDetails, setProjectDetails] = useState(null);
    const [selectedLayerId, setSelectedLayerId] = useState(null);
    const selectedLayerIdRef = useRef(null);

    const [nearmapSurveys, setNearmapSurveys] = useState([]);
    const [selectedSurveyId, setSelectedSurveyId] = useState(null);

    // Add new state alongside nearmapSurveys/selectedSurveyId
const [availableContentTypes, setAvailableContentTypes] = useState(new Set());

    const [showNearmap, setShowNearmap] = useState(true);
    const [isNearmapLoading, setIsNearmapLoading] = useState(false);

    const [bufferReadyToken, setBufferReadyToken] = useState(0);

    const polygonsRef = useRef([]);
    const linesRef = useRef([]);
    const pointsRef = useRef([]);
    const zonesRef = useRef([]);
    const propertyRef = useRef(property);

    const [featureConfigMap, setFeatureConfigMap] = useState({});
    const featureConfigMapRef = useRef({});
    const orderTrackerRef = useRef([]);
    const layerRegistryRef = useRef({});
    const bufferLayerRef = useRef(null);
    const nearmapClipLayerRef = useRef(null);

    const bufferedFeatureRef = useRef(null);

    const [nearmapDirection, setNearmapDirection] = useState('Vert');

const availableContentTypesForSelectedSurvey = React.useMemo(() => {
    const selected = nearmapSurveys.find((s) => s.id === selectedSurveyId);
    const tiles = selected?.resources?.tiles || [];
    return new Set(tiles.map((t) => t.type).filter(Boolean));
}, [nearmapSurveys, selectedSurveyId]);

 const fetchNearmapCoverage = async (feat) => {
    try {
        const centroid = turf.centroid(feat);
        const [lng, lat] = centroid.geometry.coordinates;
        const res = await fetch(NEARMAP_COVERAGE_URL(lat, lng));
        const json = await res.json();
        const surveys = json?.surveys || [];
        setNearmapSurveys(surveys);
        if (surveys.length > 0) setSelectedSurveyId(surveys[0].id);
    } catch (e) {
        console.error('Nearmap coverage fetch failed:', e);
        setNearmapSurveys([]);
    }
};


useEffect(() => {
    const map = mapInstance.current;
    if (!map || typeof map.setBearing !== 'function') return;
    const deg = showNearmap ? (BEARING_BY_DIRECTION[nearmapDirection] ?? 0) : 0;
    map.setBearing(deg);
}, [nearmapDirection, showNearmap]);


    // Mounts/removes the Nearmap layer when the toggle or selected survey
    // date changes
 useEffect(() => {
    const map = mapInstance.current;
    if (!map || !bufferedFeatureRef.current) return;

    if (!showNearmap) {
        if (nearmapClipLayerRef.current) {
            map.removeLayer(nearmapClipLayerRef.current);
            nearmapClipLayerRef.current = null;
            window._nearmapClipLayerRef = null; // ← ADD
        }
        setIsNearmapLoading(false);
        return;
    }

    if (nearmapClipLayerRef.current) {
        nearmapClipLayerRef.current.setSurveyId(selectedSurveyId);
        nearmapClipLayerRef.current.setDirection(nearmapDirection);
        return;
    }

    const clipLayer = new NearmapClipLeafletLayer(
        bufferedFeatureRef.current,
        selectedSurveyId,
        null,
        (loading) => setIsNearmapLoading(loading),
        nearmapDirection
    );
    clipLayer.addTo(map);
    nearmapClipLayerRef.current = clipLayer;
    window._nearmapClipLayerRef = clipLayer; // ← ADD — lets ExportReport.js find it without prop drilling
}, [showNearmap, selectedSurveyId, bufferReadyToken, nearmapDirection]);










    useEffect(() => { featureConfigMapRef.current = featureConfigMap; }, [featureConfigMap]);

    const getFeatureConfig = (name) => {
        const cfg = featureConfigMapRef.current[name];
        if (cfg) return cfg;
        return { color: getFallbackColor(name), opacity: 0.6, order: 100 };
    };

    useEffect(() => { polygonsRef.current = polygons; }, [polygons]);
    useEffect(() => { linesRef.current = lines; }, [lines]);
    useEffect(() => { pointsRef.current = points; }, [points]);
    useEffect(() => { zonesRef.current = zones; }, [zones]);
    useEffect(() => { propertyRef.current = property; }, [property]);

    useEffect(() => {
        selectedLayerIdRef.current = selectedLayerId;
    }, [selectedLayerId]);

    const userToken = localStorage.getItem('userToken');

    const fetchFeatureConfig = async () => {
        try {
            const res = await axios.get(`${baseurl}getFeatureConfig`, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            if (res.data.success) {
                const map = {};
                res.data.data.forEach(row => {
                    map[row.feature_name] = {
                        color: row.color,
                        opacity: parseFloat(row.opacity),
                        order: row.layer_order
                    };
                });
                setFeatureConfigMap(map);
                featureConfigMapRef.current = map;
            }
        } catch (e) {
            console.error("Error fetching feature config:", e);
        }
    };

    const reorderLayers = () => {
        const tracker = [...orderTrackerRef.current].sort((a, b) => a.order - b.order);
        tracker.forEach(entry => {
            const reg = layerRegistryRef.current[entry.id];
            if (reg && reg.group && reg.group.bringToFront) reg.group.bringToFront();
        });
    };

    const registerLayer = (id, group, innerLayer, order, color, opacity, type) => {
        const existing = layerRegistryRef.current[id];
        if (existing && existing.group && mapInstance.current) {
            mapInstance.current.removeLayer(existing.group);
            orderTrackerRef.current = orderTrackerRef.current.filter(e => e.id !== id);
        }
        layerRegistryRef.current[id] = { group, innerLayer, order, color, opacity, type };
        orderTrackerRef.current.push({ id, order });
        reorderLayers();
    };

    // Initialize Leaflet map
    useEffect(() => {
        if (mapDivRef.current && !mapInstance.current) {
            const map = L.map(mapDivRef.current, {
                center: [42.333, -83.047],
                zoom: 15,
                zoomControl: false,
                attributionControl: false,
                preferCanvas: false,
                 rotate: true,          // NEW — enables leaflet-rotate's patched pan/zoom/rotate pipeline
    rotateControl: false,  // NEW — hide the plugin's own UI control, since you already have custom direction buttons
    bearing: 0, 
            });

            L.control.zoom({ position: 'topright' }).addTo(map);

            L.tileLayer(
                `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
                { tileSize: 512, zoomOffset: -1, maxZoom: 22, attribution: '' }
            ).addTo(map);

            mapInstance.current = map;
            window._leafletInstance = map;

            const tooltip = createTooltip();
            mapDivRef.current.appendChild(tooltip);
            tooltipRef.current = tooltip;

            map.on('click', () => {
                if (selectedLayerIdRef.current) highlightFeature(null);
            });

            fetchFeatureConfig().then(fetchProjectData);
        }

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, []);

    const handleCoordinateUpdate = (layerId, newCoords) => {
        if (layerId === property.id) { setProperty(prev => ({ ...prev, coords: newCoords })); return; }
        if (zones.find(z => z.id === layerId)) { setZones(prev => prev.map(z => z.id === layerId ? { ...z, coords: newCoords } : z)); return; }
        if (polygons.find(p => p.id === layerId)) { setPolygons(prev => prev.map(p => p.id === layerId ? { ...p, coords: newCoords } : p)); return; }
        if (lines.find(l => l.id === layerId)) { setLines(prev => prev.map(l => l.id === layerId ? { ...l, coords: newCoords } : l)); return; }
        if (points.find(pt => pt.id === layerId)) { setPoints(prev => prev.map(pt => pt.id === layerId ? { ...pt, coords: newCoords } : pt)); }
    };

    const addLeafletLayerRaw = (id, geometry, color, opacity = 0.8, layerName = '', areaText = '', order = 0) => {
        const map = mapInstance.current;
        if (!map) return;

        const group = L.geoJSON({ type: 'Feature', properties: { id }, geometry }, {
            style: { color, weight: 2, fillColor: color, fillOpacity: opacity },
        }).addTo(map);

        let innerLayer = null;
        group.eachLayer(l => { innerLayer = l; });

        registerLayer(id, group, innerLayer, order, color, opacity, geometry.type);

        group.on('mouseover', () => {
            map.getContainer().style.cursor = 'pointer';
            const tooltip = tooltipRef.current;
            if (tooltip) {
                tooltip.style.display = 'block';
                tooltip.innerHTML = `<span style="font-weight:600;">${layerName}</span>
                </br>
                ${areaText ? ` <span style="color:#666;font-size:11px;">${areaText}</span>` : ''}`;
            }
        });
        group.on('mousemove', (e) => {
            const tooltip = tooltipRef.current;
            if (tooltip) {
                tooltip.style.left = `${e.containerPoint.x + 12}px`;
                tooltip.style.top = `${e.containerPoint.y - 28}px`;
            }
        });
        group.on('mouseout', () => {
            map.getContainer().style.cursor = '';
            if (tooltipRef.current) tooltipRef.current.style.display = 'none';
        });
    };

    const handlePropertyOpacityChange = (newOpacity) => {
        const reg = layerRegistryRef.current['property-boundary'];
        if (reg?.innerLayer?.setStyle) reg.innerLayer.setStyle({ fillOpacity: newOpacity });
        if (reg) reg.opacity = newOpacity;
        setProperty(prev => ({ ...prev, fillOpacity: newOpacity }));
    };

    const handlePropertyColorChange = (newColor) => {
        const reg = layerRegistryRef.current['property-boundary'];
        if (reg?.innerLayer?.setStyle) reg.innerLayer.setStyle({ color: newColor, fillColor: newColor });
        if (reg) reg.color = newColor;
        setProperty(prev => ({ ...prev, color: newColor }));
    };

    const fetchProjectData = async () => {
    try {
        const res = await axios.get(`${baseurl}getProjectFilesByPropertyId/${id}`, {
            headers: { Authorization: `Bearer ${userToken}` }
        });
        if (res.data.success) {
            const { geometry, projectData, data, zones: zoneFiles } = res.data;
            setProjectDetails(projectData);

            const geoObj = Array.isArray(geometry) ? geometry[0] : geometry;

            // Support both response shapes:
            //  - FeatureCollection: { type: 'FeatureCollection', features: [...] }
            //  - Single Feature:    { type: 'Feature', geometry: {...}, properties: {} }
            let feat = null;
            if (geoObj?.type === 'FeatureCollection' && geoObj.features?.length > 0) {
                feat = geoObj.features[0];
            } else if (geoObj?.type === 'Feature' && geoObj.geometry) {
                feat = geoObj;
            }

            if (feat) {
                if (feat.geometry?.coordinates) {
                    const raw = feat.geometry.coordinates;
                    setProperty(prev => ({ ...prev, coords: feat.geometry.coordinates, sqft: parseFloat(projectData.lotArea?.replace(/,/g, '')) || 0 }));

                    const lotAreaValue = projectData?.lotArea ? String(projectData.lotArea).trim() : '';

                    addLeafletLayerRaw(
                        'property-boundary',
                        feat.geometry,
                        '#6a11cb',
                        0,
                        'Property Boundary',
                        lotAreaValue ? `${lotAreaValue} sq ft` : '',
                        0
                    );

                    const reg = layerRegistryRef.current['property-boundary'];
                    if (reg?.group && mapInstance.current) mapInstance.current.removeLayer(reg.group);

                    // Buffer around the parcel boundary — outline only, larger dashes
                    try {
                        const bufferedFeature = turf.buffer(feat, BUFFER_METERS, { units: 'meters' });
                        if (bufferedFeature) {
                            if (bufferLayerRef.current) mapInstance.current.removeLayer(bufferLayerRef.current);
                            const bufferLayer = L.geoJSON(bufferedFeature, {
                                style: { color: '#00e5ff', weight: 2, dashArray: '14,10', fillColor: '#00e5ff', fillOpacity: 0 }
                            }).addTo(mapInstance.current);
                            bufferLayerRef.current = bufferLayer;

                            bufferedFeatureRef.current = bufferedFeature;
                            setBufferReadyToken((t) => t + 1);

                            fetchNearmapCoverage(feat);

                            const bufBounds = bufferLayer.getBounds();
                            if (bufBounds.isValid()) mapInstance.current.fitBounds(bufBounds, { padding: [30, 30], maxZoom: 18 });
                        }
                    } catch (bufErr) {
                        console.error('Buffer error:', bufErr);
                    }

                    const firstRing = feat.geometry.type === "MultiPolygon" ? raw[0][0] : raw[0];
                    const leafletCoords = firstRing.map(c => [c[0], c[1]]);
                    zoomToFeature(leafletCoords);
                }
            }

            const newPolygons = [], newLines = [], newPoints = [], groupColorMap = {};

            for (const item of data) {
                try {
                    const geoRes = await fetch(`${imgurl}${item.file_path}`);
                    const geoJson = await geoRes.json();
                    if (!groupColorMap[item.feature_name]) groupColorMap[item.feature_name] = getFeatureConfig(item.feature_name);
                    const { color: groupSharedColor, opacity: groupSharedOpacity, order: groupSharedOrder } = groupColorMap[item.feature_name];

                    if (geoJson.features) {
                        geoJson.features.forEach((feature, index) => {
                            const type = feature.geometry.type;
                            let finalCoords = null;

                            if (type === "Point") {
                                finalCoords = [feature.geometry.coordinates[0], feature.geometry.coordinates[1]];
                            } else if (type === "MultiPolygon") {
                                finalCoords = feature.geometry.coordinates[0].map(ring => ring.map(c => [c[0], c[1]]));
                            } else if (type === "Polygon") {
                                finalCoords = feature.geometry.coordinates.map(ring => ring.map(c => [c[0], c[1]]));
                            } else if (type === "LineString") {
                                finalCoords = feature.geometry.coordinates.map(c => [c[0], c[1]]);
                            } else if (type === "MultiLineString") {
                                finalCoords = feature.geometry.coordinates.flatMap(seg => seg.map(c => [c[0], c[1]]));
                            }

                            if (!finalCoords) return;

                            const layerId = `${item.feature_name}-${index}`;
                            const formattedItem = {
                                id: layerId,
                                name: item.feature_name,
                                featureLabel: `${item.feature_name} ${index + 1}`,
                                sqft: 0, acre: 0,
                                color: groupSharedColor,
                                visible: true,
                                coords: finalCoords,
                                unit: 'sqft'
                            };

                            if (type === "LineString" || type === "MultiLineString") {
                                formattedItem.feet = 0;
                                newLines.push(formattedItem);
                                addLeafletLayer(layerId, 'LineString', feature.geometry, groupSharedColor, groupSharedOpacity, item.feature_name, formattedItem.featureLabel, null, groupSharedOrder);
                            } else if (type === "Point") {
                                newPoints.push(formattedItem);
                                addLeafletLayer(layerId, 'Point', feature.geometry, groupSharedColor, groupSharedOpacity, item.feature_name, formattedItem.featureLabel, null, groupSharedOrder);
                            } else {
                                newPolygons.push(formattedItem);
                                addLeafletLayer(layerId, 'Polygon', feature.geometry, groupSharedColor, groupSharedOpacity, item.feature_name, formattedItem.featureLabel, calculatePolygonAreaSqft(finalCoords), groupSharedOrder);
                            }
                        });
                    }
                } catch (e) { console.error("Error delivery file:", e); }
            }

            setPolygons(newPolygons);
            setLines(newLines);
            setPoints(newPoints);

            const newZones = [];
            for (const zone of zoneFiles) {
                try {
                    const geoRes = await fetch(`${imgurl}${zone.file_path}`);
                    const geoJson = await geoRes.json();
                    if (geoJson.features) {
                        geoJson.features.forEach((feature, index) => {
                            let zCoords = null;
                            const type = feature.geometry.type;
                            if (type === "MultiPolygon") {
                                zCoords = feature.geometry.coordinates[0].map(ring => ring.map(c => [c[0], c[1]]));
                            } else if (type === "Polygon") {
                                zCoords = feature.geometry.coordinates.map(ring => ring.map(c => [c[0], c[1]]));
                            }

                            if (zCoords) {
                                const zoneId = `zone-${zone.id}-${index}`;
                                const zoneLabel = `${zone.zone_name} ${index + 1}`;
                                const zoneCfg = getFeatureConfig(zone.zone_name);
                                const zoneColor = '#06B6D4';
                                const zoneOpacity = 0.25;
                                const zoneSqft = calculatePolygonAreaSqft(zCoords);
                                const zoneAcre = zoneSqft / 43560;
                                const zoneItem = {
                                    id: zoneId,
                                    name: zone.zone_name,
                                    sqft: zoneSqft,
                                    acre: zoneAcre,
                                    color: zoneColor,
                                    visible: true,
                                    coords: zCoords,
                                    unit: 'sqft'
                                };
                                newZones.push(zoneItem);
                                addLeafletLayer(
                                    zoneId,
                                    'Polygon',
                                    feature.geometry,
                                    zoneColor,
                                    zoneOpacity,
                                    zone.zone_name,
                                    zoneLabel,
                                    zoneSqft,
                                    zoneCfg.order
                                );
                            }
                        });
                    }
                } catch (e) { console.error("Error zone file:", e); }
            }
            setZones(newZones);
        }
    } catch (err) { Swal.fire('Error', 'Failed to load project layers', 'error'); }
};




    const addLeafletLayer = (id, type, geometry, color, opacity = 0.8, layerName = '', featureLabel = '', areaSqft = null, order = 100) => {
        const map = mapInstance.current;
        if (!map) return;

        let style, pointToLayer;
        if (type === 'LineString' || type === 'MultiLineString') {
            style = { color, weight: 3, opacity };
        } else if (type === 'Point') {
            style = {};
            pointToLayer = (feature, latlng) => L.circleMarker(latlng, { radius: 6, fillColor: color, color: '#fff', weight: 1, fillOpacity: 1 });
        } else {
            style = { color, weight: 1.5, fillColor: color, fillOpacity: opacity };
        }

        const group = L.geoJSON({ type: 'Feature', properties: { id }, geometry }, { style, pointToLayer }).addTo(map);

        let innerLayer = null;
        group.eachLayer(l => { innerLayer = l; });

        registerLayer(id, group, innerLayer, order, color, opacity, type);

        const tooltip = tooltipRef.current;

        group.on('mouseover', () => {
            map.getContainer().style.cursor = 'pointer';
            if (tooltip) {
                tooltip.style.display = 'block';
                const sqft = (areaSqft !== null && areaSqft > 0) ? areaSqft : 0;
                const acres = sqft / 43560;
                const cleanName = (featureLabel || layerName).replace(/\s*\d+$/, '');
                const cleanId = String(id).replace(/^.*-/, '');
                const areaLine = sqft > 0
                    ? `<div style="font-size:10px;color:#444;margin-top:0px;">
                    <div style="line-height: 15px;">${sqft.toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})} sqft</div>
                    <div style="line-height: 15px;">${acres.toFixed(3)} ac</div>
                    </div>`
                    : `<div style="font-size:10px;color:#aaa;margin-top:3px;">No area data</div>`;
                tooltip.innerHTML = `<div style="font-size:10px;font-weight:700;color:#131313;line-height: 15px;">
                ${cleanName}
                <span style="color:#888;font-weight:400;">(${cleanId})</span>
                </div>
                ${areaLine}`;
            }
        });

        group.on('mousemove', (e) => {
            if (tooltip) {
                tooltip.style.left = `${e.containerPoint.x + 12}px`;
                tooltip.style.top = `${e.containerPoint.y - 28}px`;
            }
        });

        group.on('mouseout', () => {
            map.getContainer().style.cursor = '';
            if (tooltip) tooltip.style.display = 'none';
        });

        group.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            highlightFeature(id);
            setSelectedLayerId(id);
        });
    };

    const toggleVisibility = (id, type) => {
        const map = mapInstance.current;
        const reg = layerRegistryRef.current[id];
        if (!map || !reg) return;

        if (type === 'property') {
            const isVisible = !property.visible;
            if (isVisible) { reg.group.addTo(map); reorderLayers(); }
            else map.removeLayer(reg.group);

            if (isVisible && reg.innerLayer?.setStyle) {
                reg.innerLayer.setStyle({ color: property.color, fillColor: property.color });
            }

            setProperty(prev => ({ ...prev, visible: isVisible }));
            return;
        }

        const setterMap = { zone: setZones, line: setLines, polygon: setPolygons, point: setPoints };
        const setter = setterMap[type];
        if (!setter) return;

        setter(prev => prev.map(item => {
            if (item.id === id) {
                const nextVis = !item.visible;
                if (nextVis) { reg.group.addTo(map); reorderLayers(); }
                else map.removeLayer(reg.group);
                return { ...item, visible: nextVis };
            }
            return item;
        }));
    };

    const zoomToFeature = (coords, featureId = null) => {
        const map = mapInstance.current;
        if (!map || !coords || coords.length === 0) return;

        const flattenToPoints = (input) => {
            const out = [];
            const recurse = (node) => {
                if (!Array.isArray(node)) return;
                if (typeof node[0] === 'number' && typeof node[1] === 'number') {
                    out.push(node);
                } else {
                    node.forEach(recurse);
                }
            };
            recurse(input);
            return out;
        };

        let points;
        if (typeof coords[0] === 'number') {
            points = [coords];
        } else {
            points = flattenToPoints(coords);
        }

        if (points.length === 0) return;

        const latLngs = points.map(c => [c[1], c[0]]);

        if (latLngs.length === 1) {
            map.setView(latLngs[0], 18);
        } else {
            const bounds = L.latLngBounds(latLngs);
            map.fitBounds(bounds, { padding: [30, 30], maxZoom: 18 });
        }
        if (featureId) highlightFeature(featureId);
    };

    const handleColorChange = (name, newColor) => {
        polygons.forEach(p => {
            if (p.name === name) {
                const reg = layerRegistryRef.current[p.id];
                if (reg?.innerLayer?.setStyle) reg.innerLayer.setStyle({ color: newColor, fillColor: newColor });
                if (reg) reg.color = newColor;
            }
        });
        setPolygons(prev => prev.map(p => p.name === name ? { ...p, color: newColor } : p));
    };

    const handlePointColorChange = (groupName, newColor) => {
        points.forEach(pt => {
            if (pt.name === groupName) {
                const reg = layerRegistryRef.current[pt.id];
                if (reg?.innerLayer?.setStyle) reg.innerLayer.setStyle({ fillColor: newColor });
                if (reg) reg.color = newColor;
            }
        });
        setPoints(prev => prev.map(pt => pt.name === groupName ? { ...pt, color: newColor } : pt));
    };

    const handleZoneColorChange = (id, newColor) => {
        const reg = layerRegistryRef.current[id];
        if (reg?.innerLayer?.setStyle) reg.innerLayer.setStyle({ color: newColor, fillColor: newColor });
        if (reg) reg.color = newColor;
        setZones(prev => prev.map(z => z.id === id ? { ...z, color: newColor } : z));
    };

    const handleLineColorChange = (groupName, newColor) => {
        lines.forEach(l => {
            if (l.name === groupName) {
                const reg = layerRegistryRef.current[l.id];
                if (reg?.innerLayer?.setStyle) reg.innerLayer.setStyle({ color: newColor });
                if (reg) reg.color = newColor;
            }
        });
        setLines(prev => prev.map(l => l.name === groupName ? { ...l, color: newColor } : l));
    };

    const toggleAllLinesVisibility = (targetVisible) => {
        lines.forEach(l => { if (l.visible !== targetVisible) toggleVisibility(l.id, 'line'); });
    };

    const toggleAllPolygonsVisibility = (targetVisible) => {
        polygons.forEach(p => { if (p.visible !== targetVisible) toggleVisibility(p.id, 'polygon'); });
    };

    const highlightFeature = (layerId) => {
        const prevSelectedId = selectedLayerIdRef.current;

        if (prevSelectedId) {
            const prevReg = layerRegistryRef.current[prevSelectedId];
            if (prevReg?.innerLayer?.setStyle) {
                if (prevReg.type === 'Point') {
                    prevReg.innerLayer.setStyle({ fillColor: prevReg.color, color: '#fff' });
                } else if (prevReg.type === 'LineString' || prevReg.type === 'MultiLineString') {
                    prevReg.innerLayer.setStyle({ color: prevReg.color, weight: 3, opacity: 1 });
                } else {
                    prevReg.innerLayer.setStyle({ color: prevReg.color, weight: 1.5, fillColor: prevReg.color, fillOpacity: prevReg.opacity });
                }
            }
        }

        if (!layerId) {
            selectedLayerIdRef.current = null;
            setSelectedLayerId(null);
            return;
        }

        const reg = layerRegistryRef.current[layerId];
        if (reg?.innerLayer?.setStyle) {
            if (reg.type === 'Point') {
                reg.innerLayer.setStyle({ fillColor: '#e7ff13', color: '#e7ff13' });
            } else if (reg.type === 'LineString' || reg.type === 'MultiLineString') {
                reg.innerLayer.setStyle({ color: '#e7ff13', weight: 6 });
            } else {
                reg.innerLayer.setStyle({ color: '#e7ff13', weight: 3, fillColor: '#e7ff13', fillOpacity: 0.6 });
            }
            selectedLayerIdRef.current = layerId;
            setSelectedLayerId(layerId);
        }
    };

    return (
        <div className="gis-dashboard-wrapper">
            <ReportTop projectDetails={projectDetails} id={id} />
            <div className="dashboard-container">
                <div className="left-panel">
                    <ReportLeftData
                        property={property}
                        toggleVisibility={() => toggleVisibility(property.id, 'property')}
                        toggleUnit={() => setProperty(prev => ({ ...prev, unit: prev.unit === 'sqft' ? 'acre' : 'sqft' }))}
                        onOpacityChange={handlePropertyOpacityChange}
                        onColorChange={handlePropertyColorChange}
                    />
                    <ReportZone
                        zones={zones}
                        toggleVisibility={(id) => toggleVisibility(id, 'zone')}
                        handleColorChange={handleZoneColorChange}
                        zoomToFeature={zoomToFeature}
                    />
                    <PolygonCoverage
                        polygons={polygons}
                        toggleVisibility={(id) => toggleVisibility(id, 'polygon')}
                        toggleAllVisibility={toggleAllPolygonsVisibility}
                        handleColorChange={handleColorChange}
                        zoomToFeature={zoomToFeature}
                    />
                    <LineCoverage
                        lines={lines}
                        setLines={setLines}
                        toggleVisibility={(id) => toggleVisibility(id, 'line')}
                        toggleAllVisibility={toggleAllLinesVisibility}
                        handleLineColorChange={handleLineColorChange}
                        zoomToFeature={zoomToFeature}
                    />
                    <PointCoverage
                        points={points}
                        toggleVisibility={(id) => toggleVisibility(id, 'point')}
                        handlePointColorChange={handlePointColorChange}
                        zoomToFeature={zoomToFeature}
                    />
                </div>
                <div className="right-panel" style={{ position: 'relative' }}>

                  
{nearmapSurveys.length > 0 && (
    <div style={{
        position: 'absolute', bottom: '10px', left: '10px', zIndex: 1000,
        background: '#fff', borderRadius: '10px', padding: '10px 12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)', fontFamily: 'sans-serif',
    }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, marginBottom: showNearmap ? '8px' : 0 }}>
            <input
                type="checkbox"
                checked={showNearmap}
                onChange={(e) => setShowNearmap(e.target.checked)}
                style={{ accentColor: '#6a11cb' }}
            />
          Toggel Map
        </label>
        {/* {showNearmap && (
            <>
                <select
                    value={selectedSurveyId || ''}
                    onChange={(e) => setSelectedSurveyId(e.target.value)}
                    style={{ width: '100%', padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }}
                >
                    {nearmapSurveys.map((s) => (
                        <option key={s.id} value={s.id}>{s.captureDate || s.date || s.id}</option>
                    ))}
                </select>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
            {NEARMAP_DIRECTIONS.map((d) => (
                <button
                    key={d.key}
                    onClick={() => setNearmapDirection(d.key)}
                    style={{
                        fontSize: '11px', padding: '4px 8px', borderRadius: '6px',
                        border: '1px solid #ddd', cursor: 'pointer',
                        background: nearmapDirection === d.key ? '#6a11cb' : '#fff',
                        color: nearmapDirection === d.key ? '#fff' : '#333',
                    }}
                >
                    {d.label}
                </button>
            ))}
        </div>

                {(() => {
                    const selected = nearmapSurveys.find((s) => s.id === selectedSurveyId);
                    const vintage = selected?.captureDate || selected?.date || null;
                    return vintage ? (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '6px' }}>
                            Vintage: {vintage}
                        </div>
                    ) : null;
                })()}
            </>
        )} */}


       {showNearmap && (
    <>
        {/* <select
            value={selectedSurveyId || ''}
            onChange={(e) => {
                setSelectedSurveyId(e.target.value);
                setNearmapDirection('Vert'); // reset — new survey may not have the previously selected direction
            }}
            style={{ width: '100%', padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px', marginBottom: '6px' }}
        >
            {nearmapSurveys.map((s) => (
                <option key={s.id} value={s.id}>{s.captureDate}</option>
            ))}
        </select> */}

        {/* <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
            {NEARMAP_DIRECTIONS.map((d) => {
                const isAvailable = availableContentTypesForSelectedSurvey.has(d.key);
                if (!isAvailable) return null;
                return (
                    <button
                        key={d.key}
                        onClick={() => setNearmapDirection(d.key)}
                        style={{
                            fontSize: '11px', padding: '4px 8px', borderRadius: '6px',
                            border: '1px solid #ddd', cursor: 'pointer',
                            background: nearmapDirection === d.key ? '#6a11cb' : '#fff',
                            color: nearmapDirection === d.key ? '#fff' : '#333',
                        }}
                    >
                        {d.label}
                    </button>
                );
            })}
        </div> */}

        {/* {NEARMAP_DIRECTIONS.filter((d) => d.key !== 'Vert' && availableContentTypesForSelectedSurvey.has(d.key)).length === 0 && (
            <div style={{ fontSize: '11px', color: '#999', marginTop: '6px', fontStyle: 'italic' }}>
                No oblique image found for this capture date
            </div>
        )} */}

        {(() => {
            const selected = nearmapSurveys.find((s) => s.id === selectedSurveyId);
            const vintage = selected?.captureDate || selected?.date || null;
            return vintage ? (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '6px' }}>
                    Vintage: {vintage}
                </div>
            ) : null;
        })()}
    </>
)}


    </div>
)}

                    {isNearmapLoading && (
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 999, pointerEvents: 'none',
                        }}>
                            <div style={{
                                background: 'rgba(255,255,255,0.92)', borderRadius: '12px',
                                padding: '14px 22px', display: 'flex', alignItems: 'center', gap: '10px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)', fontFamily: 'sans-serif',
                            }}>
                                <div style={{
                                    width: '18px', height: '18px', borderRadius: '50%',
                                    border: '3px solid #e2e2e2', borderTopColor: '#6a11cb',
                                    animation: 'nearmap-spin 0.8s linear infinite',
                                }} />
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                                    Loading HD imagery…
                                </span>
                            </div>
                        </div>
                    )}

                    <div id="map-container" style={{ height: "100%", width: "100%" }}>
                        <div ref={mapDivRef} style={{ height: "100%", width: "100%" }}></div>
                    </div>
                </div>
            </div>

            <ExportReport
                projectDetails={projectDetails}
                proid={id}
                property={property}
                zones={zones}
                polygons={polygons}
                lines={lines}
                points={points}
                layerRefs={layerRegistryRef}
            />

            <style>
                {`
                @keyframes nearmap-spin {
                    to { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

export default ReportNew;